import React, { useEffect } from 'react';
import { Avatar, Breadcrumbs, ClanNavigation, DateTime, Pagination } from '@/components';
import { useProfile } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { guildActions } from '@/store/features';
import cls from 'classnames';
import { useSearchParamsAsObject } from '@/hooks/useSearchParamsAsObject.hook';
import {
  ClanJoinRequestDtoRead,
  ClanMemberWithStatsDtoRead,
  useClanControllerApproveJoinRequestMutation,
  useClanControllerChangeRoleMutation,
  useClanControllerFindMembersQuery,
  useClanControllerFindOneQuery,
  useClanControllerGetJoinRequestsQuery,
  useClanControllerRemoveJoinRequestMutation,
  useClanControllerRemoveMemberMutation,
} from '@/store/services/MidleNestApi.service';

interface GuildProfileViewProps {
  mode?: string;
}

const GuildRoles: string[] = ['newbie', 'member', 'officer', 'secondLeader'];

export default function ClanProfileView({ mode }: GuildProfileViewProps) {
  const { guildId: id } = useParams();

  const { data, isLoading } = useClanControllerFindOneQuery(id ? id : '');
  const {
    profile: { id: userId, clan: myClan },
    reloadProfile,
  } = useProfile();

  const [leaveClan] = useClanControllerRemoveMemberMutation();
  const [approveJoinReuqest, { isLoading: respondingApprove }] =
    useClanControllerApproveJoinRequestMutation();
  const [removeJoinRequest, { isLoading: respondingRemove }] =
    useClanControllerRemoveJoinRequestMutation();

  const dispatch = useDispatch();
  const { t } = useTranslation();

  // const { page, limit, setPage } = usePagination();
  const { searchParams } = useSearchParamsAsObject<{
    page: number;
    limit: number;
  }>();
  const { page = 1, limit = 10 } = searchParams;

  const { data: members } = useClanControllerFindMembersQuery(
    {
      page,
      limit,
      id: id as string,
    },
    { skip: !id },
  );

  const { data: invites } = useClanControllerGetJoinRequestsQuery({
    status: 'invites',
  });

  const { data: joinRequests } = useClanControllerGetJoinRequestsQuery({
    status: 'join-requests',
  });

  const openGuildJoinModal = React.useCallback(
    ({ guildId, guildName }: { guildId: string; guildName: string }) =>
      dispatch(guildActions.setJoinModal({ guildId, guildName, show: true })),
    [dispatch],
  );

  const navigate = useNavigate();

  const amiInAClan = !!myClan?.id;
  const isMyClan = amiInAClan && data?.id === myClan?.id;
  const isLeader = userId === data?.leaderId;

  const leaveClanHandler = React.useCallback(async () => {
    if (!isMyClan) return;
    await leaveClan(userId).then(() => {
      gtag('event', 'clan-leave', { clan: data?.id, user: userId });
      navigate('/clans');
    });
  }, [leaveClan, isMyClan]);

  const respondInviteHandlerAsUser = React.useCallback(
    async (id: string, approve: boolean) => {
      if (amiInAClan || respondingApprove || respondingRemove) return;

      if (approve) {
        await approveJoinReuqest(id);
        reloadProfile();
        gtag('event', 'clan-join', { clan: data?.id, user: userId });
      } else await removeJoinRequest(id);
    },
    [amiInAClan, respondingApprove, respondingRemove],
  );

  const respondInviteHandlerAsClan = React.useCallback(
    async (id: string, approve: boolean) => {
      if (respondingApprove || respondingRemove) return;

      if (approve) {
        await approveJoinReuqest(id);
        gtag('event', 'clan-join', { clan: data?.id, user: userId });
      } else {
        await removeJoinRequest(id);
        // gtag('event', 'clan-denied', { guild: data?.id, user: userId })
      }
    },
    [amiInAClan, respondingApprove, respondingRemove],
  );

  useEffect(() => {
    // if (userId)
    gtag('event', 'clan-view', { clan: id, user: userId });
  }, []);

  return (
    <>
      {!id ? (
        <Navigate to='/clans' />
      ) : !isLoading && !data ? (
        <Navigate to='/clans' />
      ) : (
        <div className='clan max-w-7xl px-4 py-8 mx-auto space-y-4 xl:space-y-6'>
          <Breadcrumbs
            paths={[
              { href: '/clans', text: t('Clans') },
              { href: '/clans/' + id, text: data?.name },
            ]}
          />
          <div className='flex w-full flex-col gap-y-8 xl:flex-row xl:gap-x-16 p-4 lg:p-8 bg-white dark:bg-[#242424] dark:text-white shadow-sm rounded-3xl'>
            <div className='flex flex-1 flex-col gap-y-8'>
              <header className='flex flex-col sm:flex-row gap-y-4 w-full justify-between items-center'>
                <h2 className='flex gap-x-4 items-center text-base leading-6 font-medium'>
                  <div className='relative w-10 h-10 rounded-full'>
                    {data?.avatar?.src && (
                      <Avatar
                        name={String(data.name)}
                        src={data.avatar?.src}
                        className='h-10 w-10'
                      />
                    )}
                  </div>
                  {data?.name}
                </h2>

                <div className='flex gap-x-2 items-center'>
                  {!amiInAClan &&
                    (data?.isInvited ? (
                      <>
                        <button
                          type='button'
                          className='w-32 py-2 bg-green-600 rounded-xl text-sm leading-5 font-medium text-white'
                          onClick={() => respondInviteHandlerAsUser(data?.inviteId ?? '', true)}
                        >
                          {t('Accept Invite')}
                        </button>
                        <button
                          type='button'
                          className='w-32 py-2 bg-red-600 rounded-xl text-sm leading-5 font-medium text-white'
                          onClick={() => respondInviteHandlerAsUser(data?.inviteId ?? '', false)}
                        >
                          {t('Decline')}
                        </button>
                      </>
                    ) : (
                      <>
                        {data?.isJoinRequestSent ? (
                          <button
                            type='button'
                            className='w-32 py-2 bg-red-600 rounded-xl text-sm leading-5 font-medium text-white'
                            onClick={() =>
                              respondInviteHandlerAsUser(data?.joinRequestId ?? '', false)
                            }
                          >
                            {t('Cancel Request')}
                          </button>
                        ) : (
                          <button
                            type='button'
                            className='w-32 py-2 bg-slate-900 rounded-xl text-sm leading-5 font-medium text-white disabled:grayscale'
                            disabled={data?.isJoinRequestSent}
                            onClick={() =>
                              openGuildJoinModal({
                                guildId: data?.id || '',
                                guildName: data?.name || '',
                              })
                            }
                          >
                            {t('Join Clan')}
                          </button>
                        )}
                      </>
                    ))}
                  {isMyClan && (
                    <>
                      <Link
                        to={`/clans/${id}/invite`}
                        className='w-32 py-2 bg-slate-900 rounded-xl text-sm text-center leading-5 font-medium text-white dark:bg-[#FFDA68] dark:text-[#151515]'
                      >
                        {t('Invite')}
                      </Link>
                      <button
                        type='button'
                        className='w-32 py-2 border border-gray-300 rounded-xl text-sm leading-5 font-medium text-slate-900 dark:border-[#FFDA68] dark:text-[#FFDA68]'
                        onClick={leaveClanHandler}
                      >
                        {t('Leave Clan')}
                      </button>
                    </>
                  )}
                  {isMyClan && isLeader && (
                    <Link
                      to={`/clans/${id}/settings`}
                      className='p-2 text-sm align-middle leading-5 font-medium text-slate-900 dark:text-white'
                    >
                      <i className='icon-gear' />
                    </Link>
                  )}
                </div>
              </header>
              <p className='text-sm leading-5 font-light text-gray-500 text-justify dark:text-white'>
                {data?.message}
              </p>
            </div>
            <div className='flex flex-1 gap-x-4 flex-col gap-y-4 sm:flex-row divide-y text-center sm:text-left sm:divide-x sm:divide-y-0 divide-gray-200'>
              <div className='flex flex-1 shrink-0 flex-col gap-y-2 py-4 sm:py-0 sm:px-8'>
                <i className='icon-people text-3xl dark:text-gray-200' />
                <h3 className='text-base leading-6 font-medium text-slate-700 dark:text-white'>
                  {t('Members')}
                </h3>
                <span className='mt-4 text-5xl leading-none font-bold text-blue-500 dark:text-[#FFDA68]'>
                  {data?.membersCount}
                </span>
                <p className='text-xs leading-4 font-normal h-8 max-h-8 text-gray-500 dark:text-white'>
                  {t('Total members of the clan')}
                </p>
              </div>
              <div className='flex flex-1 shrink-0 flex-col gap-y-2 py-4 sm:py-0 sm:px-8'>
                <i className='icon-star-down text-3xl dark:text-gray-200' />
                <h3 className='text-base leading-6 font-medium text-slate-700 dark:text-white'>
                  {t('Points')}
                </h3>
                <span className='mt-4 text-5xl leading-none font-bold text-blue-500 dark:text-[#FFDA68]'>
                  {data?.leaderboardActivity?.totalXp}
                </span>
                <p className='text-xs leading-4 font-normal h-8 max-h-8 text-gray-500 dark:text-white'>
                  {t('Points won by the clan')}
                </p>
              </div>
              <div className='flex flex-1 shrink-0 flex-col gap-y-2 py-4 sm:py-0 sm:px-8'>
                <i className='icon-activity text-3xl dark:text-gray-200' />
                <h3 className='text-base leading-6 font-medium text-slate-700 dark:text-white'>
                  {t('Activity')}
                </h3>
                <span className='mt-4 text-5xl leading-none font-bold text-blue-500 dark:text-[#FFDA68]'>
                  {data?.leaderboardActivity?.activityRating}
                </span>
                <p className='text-xs leading-4 font-normal h-8 max-h-8 text-gray-500 dark:text-white'>
                  {t('Activity rating of the clan')}
                </p>
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-y-8 p-4 lg:p-8 w-full bg-white dark:bg-[#242424] dark:text-white shadow-sm rounded-3xl'>
            <div>
              <ClanNavigation
                joinRequests={(joinRequests?.meta?.total || 0) > 0}
                invites={(invites?.meta?.total || 0) > 0}
              />
            </div>

            {(mode === 'join-requests' || mode === 'invites') && (
              <>
                <table className='table-auto min-w-full divide-y divide-gray-200'>
                  <thead>
                    <tr className='text-center text-xs sm:text-sm font-semibold'>
                      <td className='hidden sm:table-cell ' />
                      <td className='text-left p-3'>{t('Name')}</td>
                      <td className='hidden sm:table-cell'>{t('Monthly Points')}</td>
                      <td className='hidden sm:table-cell'>{t('Total Points')}</td>
                      <td className={'hidden sm:table-cell'}>{t('Avg. Points per Day')}</td>
                      <td className='hidden sm:table-cell'>{t('Days')}</td>
                      <td />
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-200 dark:divide-[#FFDA68]/30 bg-white dark:bg-transparent'>
                    {mode === 'join-requests' &&
                      joinRequests?.list?.map((i, k) => (
                        <RequestAndInviteUserRow
                          key={'joinRequests_' + i?.id}
                          i={i}
                          k={k}
                          isInvite={false}
                          accept={() => respondInviteHandlerAsClan(i?.id, true)}
                          reject={() => respondInviteHandlerAsClan(i?.id, false)}
                        />
                      ))}
                    {mode === 'invites' &&
                      invites?.list?.map((i, k) => (
                        <RequestAndInviteUserRow
                          key={'invites_' + i?.id}
                          i={i}
                          k={k}
                          isInvite={true}
                          accept={() => respondInviteHandlerAsClan(i?.id, true)}
                          reject={() => respondInviteHandlerAsClan(i?.id, false)}
                        />
                      ))}
                  </tbody>
                </table>
                <Pagination page={page} limit={limit} total={joinRequests?.meta?.total} />
              </>
            )}

            {!mode && (
              <>
                <table className='table-auto min-w-full divide-y divide-gray-200'>
                  <thead>
                    <tr className='text-center text-xs sm:text-sm font-semibold'>
                      <td className='hidden sm:table-cell ' />
                      <td className='text-left p-3'>{t('Member Name')}</td>
                      <td className='hidden sm:table-cell'>{t('Role')}</td>
                      <td className=''>{t('Monthly Points')}</td>
                      <td className=''>{t('Total Points')}</td>
                      {/* <td className='hidden sm:table-cell'>{t('Avg. Points per Day')}</td> */}
                      <td className={cls('hidden ', isLeader ? 'lg:table-cell' : 'md:table-cell')}>
                        {t('Avg. Points per Day')}
                      </td>
                      <td className='hidden sm:table-cell'>{t('Join Date')}</td>
                      <td />
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-200 dark:divide-[#FFDA68]/30 bg-white dark:bg-transparent'>
                    {members?.list?.map((i, k) => (
                      <ClanMemberRow
                        key={'members_' + i?.id}
                        i={i}
                        k={k}
                        isMyClan={isMyClan}
                        isLeader={isLeader}
                      />
                    ))}
                  </tbody>
                </table>
                <Pagination page={page} limit={limit} total={members?.meta.total} />
              </>
            )}
          </div>
          <Outlet />
        </div>
      )}
    </>
  );
}

interface ClanMemberRowRowProps {
  i: ClanMemberWithStatsDtoRead;
  k: number;
  isMyClan: boolean;
  isLeader: boolean;
}

function ClanMemberRow({ i, k, isMyClan, isLeader }: ClanMemberRowRowProps) {
  const { t } = useTranslation();

  const [removeMember, { isLoading: removing }] = useClanControllerRemoveMemberMutation();
  const [changeRole, { isLoading: changing }] = useClanControllerChangeRoleMutation();

  const removeMemberHandler = React.useCallback(
    async (id: string) => {
      if (!isMyClan || !isLeader || removing) return;
      await removeMember(id);
    },
    [removeMember, removing, isMyClan, isLeader],
  );

  const setUserRoleHandler = React.useCallback(
    async (prevRole: string, userId: string, role: string) => {
      if (changing) return;
      if (role === prevRole) return;
      await changeRole({ userId, role });
    },
    [isMyClan, isLeader, changeRole, changing],
  );

  return (
    <tr
      key={'member_' + i?.profile?.user?.username}
      className='text-center text-gray-600 dark:text-white text-xs sm:text-sm'
    >
      <td colSpan={1} className='hidden sm:table-cell'>
        {k + 1}
      </td>
      <td colSpan={1} className='flex gap-x-4 items-center text-left p-3'>
        <div>
          <Avatar
            name={String(i?.profile?.user?.username)}
            src={i?.profile?.avatar?.src}
            className='h-7 w-7 lg:h-10 lg:w-10'
          />
        </div>{' '}
        <div className='max-w-[60px] lg:max-w-[200px] truncate'>{i?.profile?.user?.username}</div>
      </td>
      <td colSpan={1} className='hidden sm:table-cell'>
        {i.role.charAt(0).toUpperCase() + i.role.slice(1).replace(/([A-Z])/g, ' $1')}
      </td>
      <td colSpan={1} className=''>
        {i?.leaderboardActivity?.currentMonthXp}
      </td>
      <td colSpan={1} className=''>
        {i?.leaderboardActivity?.totalXp}
      </td>
      <td colSpan={1} className={cls('hidden', isLeader ? 'lg:table-cell' : 'md:table-cell')}>
        {i?.leaderboardActivity && i?.leaderboardActivity?.totalXp != 0
          ? Math.round(i?.leaderboardActivity?.totalXp / (i?.leaderboardActivity?.hours / 24))
          : 0}
      </td>
      <td colSpan={1} className='hidden sm:table-cell'>
        <DateTime date={i?.joinedAt} format='date' />
      </td>
      {/* TODO: user.roles.contains('changeRole') */}
      {/* {isMyClan && isLeader && i.role !== 'leader' && ( */}
      {isMyClan && isLeader && (
        <td colSpan={1} className='space-x-2'>
          <select
            className='hidden md:inline w-min border border-gray-300 rounded-lg text-xs bg-transparent'
            defaultValue={i.role}
            // placeholder={t('Change Role')}
            disabled={changing}
            onChange={({ target }) =>
              setUserRoleHandler(
                i.role /* TODO as GamiHubBackendDomainEnumsGuildRole */,
                i.profile.id as string,
                target.value /* TODO as GamiHubBackendDomainEnumsGuildRole */,
              )
            }
          >
            {GuildRoles.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
          <button
            type='button'
            className='text-sm text-gray-500'
            onClick={() => removeMemberHandler(i.profile.id as string)}
            disabled={removing}
          >
            <i className='icon-close dark:text-white' />
          </button>
        </td>
      )}
    </tr>
  );
}

interface RequestAndInviteUserRowProps {
  isInvite: boolean;
  i: ClanJoinRequestDtoRead;
  k?: number;
  accept?: () => void;
  reject?: () => void;
}

function RequestAndInviteUserRow({
  isInvite,
  i,
  k = 0,
  accept = undefined,
  reject = undefined,
}: RequestAndInviteUserRowProps) {
  const days = Math.round(
    (new Date().getTime() - new Date(i?.profile?.user?.createdAt).getTime()) / 86400000,
  );
  const { t } = useTranslation();

  return (
    <>
      <tr key={'member_' + i?.id} className='text-center text-gray-600 text-xs sm:text-sm'>
        <td colSpan={1} className='hidden sm:table-cell'>
          {k + 1}
        </td>
        <td colSpan={1} className='flex gap-x-4 items-center text-left p-3'>
          <div className=''>
            <Avatar
              name={String(i?.profile?.user?.username)}
              src={i?.profile?.avatar?.src}
              className='h-10 w-10'
            />
          </div>{' '}
          {i?.profile?.user?.username}
        </td>
        <td colSpan={1} className=''>
          {0}
        </td>
        <td colSpan={1} className=''>
          {i?.profile?.currentXp}
        </td>
        <td colSpan={1} className=''>
          {Math.round(i?.profile?.currentXp / days)}
        </td>
        <td colSpan={1} className=''>
          {days}
        </td>
        <td className='space-x-2'>
          {accept && (
            <button
              type='button'
              onClick={() => accept()}
              className='w-5 h-5 rounded-sm text-xs text-white bg-green-500'
            >
              <i className='icon-check' />
            </button>
          )}
          {reject && (
            <button
              type='button'
              onClick={() => reject()}
              className='w-5 h-5 rounded-sm text-xs text-white bg-red-500'
            >
              <i className='icon-close' />
            </button>
          )}
        </td>
      </tr>
      {!isInvite && (
        <tr key={'member_message_' + i?.id} className='!border-0'>
          <td></td>
          <td colSpan={100}>
            <div className='p-4 bg-blue-50 rounded-md text-xs mb-2'>
              <div className='inline text-sm leading-5 font-medium'>{t('Request Message:')}</div>{' '}
              <div className='inline'>{i?.message}</div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
